import { useEffect, useState, type FormEvent } from 'react';
import { Building2, CircleCheck, CircleX, LockKeyhole, Mail } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getApiHealth } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function Login() {
  const { session } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [apiState, setApiState] = useState<{ state: 'checking' | 'online' | 'offline'; text: string }>({ state: 'checking', text: 'Checking v3 API…' });

  useEffect(() => {
    let alive = true;
    getApiHealth()
      .then((health) => {
        if (alive) setApiState({ state: 'online', text: `API online · v${health.version}` });
      })
      .catch((e) => {
        if (alive) setApiState({ state: 'offline', text: e instanceof Error ? e.message : 'API unavailable' });
      });
    return () => { alive = false; };
  }, []);

  if (session) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) setError(authError.message);
    setBusy(false);
  };

  return (
    <div className="login-page">
      <div className="login-visual">
        <div className="login-brand"><div className="brand-mark large">M</div><div><strong>MAGNA International</strong><span>Accounting v3 · Production</span></div></div>
        <div className="login-copy"><span className="eyebrow">CENTRALIZED FINANCE CONTROL</span><h1>Clean production architecture. Existing accounting data preserved.</h1><p>Expenses, payments, assets, advances, borrowings, petty cash, approvals, reports and general ledger remain connected to the existing MAGNA Supabase database.</p></div>
        <div className="login-points"><span><Building2 size={18}/> Multi-branch accounting</span><span><LockKeyhole size={18}/> Role-based approvals</span></div>
      </div>
      <div className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div><span className="eyebrow">MAGNA ACCOUNTING V3</span><h2>Sign in to continue</h2><p>Use your existing authorized MAGNA account.</p></div>
          <div className={`api-status api-status-${apiState.state}`}>
            {apiState.state === 'online' ? <CircleCheck size={16}/> : apiState.state === 'offline' ? <CircleX size={16}/> : <span className="mini-spinner"/>}
            <span>{apiState.text}</span>
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <label>Email address<div className="input-with-icon"><Mail size={18}/><input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required autoComplete="email" placeholder="name@magna.lk" /></div></label>
          <label>Password<div className="input-with-icon"><LockKeyhole size={18}/><input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required autoComplete="current-password" placeholder="••••••••" /></div></label>
          <button className="button primary large-button" disabled={busy || apiState.state === 'offline'}>{busy ? 'Signing in…' : 'Sign in'}</button>
          <small>If the API indicator is offline, deployment verification must be completed before accounting work starts.</small>
        </form>
      </div>
    </div>
  );
}
